import { createStyles, makeStyles, Theme } from '@material-ui/core';
import { Auth, I18n } from 'aws-amplify';
import { Formik } from 'formik';
import { FC, useCallback, useContext, useEffect, useState } from 'react';
import * as Yup from 'yup';

import { LOGIN_PAGE } from 'src/constants';
import { AUTH_STATES } from 'src/constants/authConsts';
import { ContextProps, PortalConfigContext, RouteContext } from 'src/context';
import history from 'src/history';
import { IAuthenticatorReturn } from 'src/legacy/components/AwsAmplify';
import Button from 'src/legacy/components/Button';
import { BaseTextField, PasswordField } from 'src/legacy/components/TextField';
import {
  getPreferredUsername,
  GetSignInMetadata,
  GetSignInMetadataInput,
} from 'src/utils/AuthUtils';
import { getCookie } from 'src/utils/StorageUtils';

import { AuthLinkActionButton } from '../AuthLinkActionButton';
import { ClientAuthContainer } from '../ClientAuthContainer';
import { authScreenStyles } from '../styles';

interface IState {
  isLoading?: boolean;
  errorMessage?: string;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    ...authScreenStyles(theme),
  }),
);

const accountFormSchema = Yup.object().shape({
  username: Yup.string()
    .email('Invalid email')
    .max(100)
    .required('Email is required'),
});

const confirmFormSchema = Yup.object().shape({
  code: Yup.string()
    .trim()
    .min(6, 'Code must be exactly 6 digits.')
    .max(6, 'Code must be exactly 6 digits.')
    .required('Code is required'),
  password: Yup.string()
    .max(256, 'Password must be less than 256 characters')
    .min(8, 'Password must be at least 8 characters')
    .matches(/[a-z]/, 'Password must have at least one lowercase char')
    .matches(/[A-Z]/, 'Password must have at least one uppercase char')
    .matches(/[0-9]+/, 'Password must have at least one number')
    .required('Password is required'),
});

const validAuthState = ['forgotPassword'];

const ForgotPasswordForm: FC<IAuthenticatorReturn> = (props) => {
  const { onStateChange, inputs, handleInputChange, authState } = props;
  const classes = useStyles();
  const context: ContextProps = useContext(RouteContext);

  // Using this state to determine which form to show to the user
  const [delivery, setDelivery] = useState<string | null>(null);

  const [state, setState] = useState<IState>({
    isLoading: false,
    errorMessage: '',
  });

  const portalConfig = useContext(PortalConfigContext);

  const handleGoToSignIn = () => {
    onStateChange(AUTH_STATES.SIGN_IN);
    history.push(LOGIN_PAGE);
  };

  useEffect(() => {
    onStateChange(AUTH_STATES.FORGOT_PASSWORD);
  }, []);

  const handleSendCode = async () => {
    const { username } = inputs;

    if (!username) return;

    if (!Auth || typeof Auth.forgotPassword !== 'function') {
      throw new Error(
        'No Auth module found, please ensure @aws-amplify/auth is imported',
      );
    }

    try {
      setState({ isLoading: true });

      // Skip Cognito's ForgotPassword API when running in Reflect e2e tests
      // to avoid hitting Cognito's built-in rate limit across test runs.
      if (getCookie('__disable_animations')) {
        setDelivery('EMAIL');
        setState({ isLoading: false });
        return;
      }

      const preferredUsername = getPreferredUsername({
        email: username,
        viewMode: portalConfig.viewMode,
        portalId: portalConfig.portalHeader,
      });
      const data = await Auth.forgotPassword(
        preferredUsername,
        GetSignInMetadata(context.query as GetSignInMetadataInput),
      );

      if (!data) return;

      setDelivery(data.CodeDeliveryDetails.DeliveryMedium);
      setState({ isLoading: false });
    } catch (error) {
      setState({
        isLoading: false,
        errorMessage:
          'We encountered an issue resetting your password. Please check your email address and try again, or contact support if the problem persists.',
      });
    }
  };

  const ConfirmForm = useCallback(
    () => (
      <Formik
        initialValues={{
          code: '',
          password: '',
        }}
        validationSchema={confirmFormSchema}
        onSubmit={async (_, { setStatus }) => {
          try {
            setState({ isLoading: true });

            const { code, password, username } = inputs;
            if (!username || !password || !code) return;

            const preferredUsername = getPreferredUsername({
              email: username,
              viewMode: portalConfig.viewMode,
              portalId: portalConfig.portalHeader,
            });
            await Auth.forgotPasswordSubmit(
              preferredUsername,
              code,
              password,
              GetSignInMetadata(context.query as GetSignInMetadataInput),
            );
            onStateChange('autoSignIn', {
              username: preferredUsername,
              password,
            });
            setState({ isLoading: false });
          } catch (error) {
            setStatus({ success: false });
            setState({ isLoading: false });
          }
        }}
      >
        {({
          values,
          errors,
          handleBlur,
          handleSubmit,
          touched,
          handleChange,
        }) => {
          const isConfirmEnabled =
            values.code && !errors.code && values.password && !errors.password;
          return (
            <form noValidate onSubmit={handleSubmit}>
              <div className={classes.fields}>
                <div>
                  <BaseTextField
                    fullWidth
                    sizeVariant="tall"
                    type="text"
                    key="code"
                    name="code"
                    variant="outlined"
                    onBlur={handleBlur}
                    onInput={handleChange}
                    onChange={handleInputChange}
                    label={I18n.get('Code')}
                    error={Boolean(touched.code && errors.code)}
                    helperText={(touched.code && errors.code) || ' '}
                    autoComplete="off"
                    autoFocus
                  />
                  <PasswordField
                    fullWidth
                    sizeVariant="tall"
                    type="password"
                    key="password"
                    name="password"
                    variant="outlined"
                    onBlur={handleBlur}
                    onInput={handleChange}
                    onChange={handleInputChange}
                    label={I18n.get('New password')}
                    error={Boolean(touched.password && errors.password)}
                    helperText={(touched.password && errors.password) || ' '}
                    autoComplete="off"
                    inputProps={{
                      autoComplete: 'new-password',
                    }}
                  />
                </div>
              </div>
              <div className="w-full mt-5">
                <Button
                  size="large"
                  className={classes.submitButton}
                  fullWidth
                  htmlId="forgot-password-submit"
                  type="submit"
                  color="primary"
                  variant="contained"
                  disabled={!isConfirmEnabled}
                  isLoading={state.isLoading}
                >
                  {I18n.get('Reset password')}
                </Button>
              </div>
            </form>
          );
        }}
      </Formik>
    ),
    [inputs, state, onStateChange],
  );

  const AccountForm = useCallback(
    () => (
      <Formik
        initialValues={{
          username: '',
        }}
        validationSchema={accountFormSchema}
        onSubmit={async (_, { setStatus, resetForm }) => {
          try {
            setState({ isLoading: true });
            await handleSendCode();
            setState({ isLoading: false });
            resetForm();
          } catch (error) {
            setStatus({ success: false });
            setState({ isLoading: false });
            resetForm();
          }
        }}
      >
        {({
          values,
          errors,
          handleBlur,
          handleSubmit,
          touched,
          handleChange,
        }) => {
          const isSendEnabled = values.username && !errors.username;

          return (
            <form noValidate onSubmit={handleSubmit}>
              <div className={classes.fields}>
                <div>
                  <BaseTextField
                    fullWidth
                    sizeVariant="tall"
                    type="text"
                    key="username"
                    name="username"
                    variant="outlined"
                    onBlur={handleBlur}
                    onInput={handleChange}
                    onChange={handleInputChange}
                    label={I18n.get('Email')}
                    error={Boolean(touched.username && errors.username)}
                    helperText={(touched.username && errors.username) || ' '}
                    autoComplete="off"
                    autoFocus
                  />
                </div>
              </div>
              <div className="mt-5">
                <Button
                  className={classes.submitButton}
                  fullWidth
                  htmlId="forgot-password-send-code"
                  type="submit"
                  color="primary"
                  variant="contained"
                  disabled={!isSendEnabled}
                  isLoading={state.isLoading}
                >
                  {I18n.get('Next')}
                </Button>
              </div>
            </form>
          );
        }}
      </Formik>
    ),
    [inputs, state],
  );

  if (!validAuthState.includes(authState)) return null;

  return (
    <ClientAuthContainer
      title="Reset your password"
      description="A verification code will be sent to your email."
    >
      <div className={classes.formContainer}>
        <div>
          {delivery ? ConfirmForm() : AccountForm()}
          <AuthLinkActionButton
            linkText="Back to sign in"
            onClick={handleGoToSignIn}
            className={classes.navigationActionsContainerMobile}
          />
        </div>
      </div>
      <AuthLinkActionButton
        linkText="Back to sign in"
        onClick={handleGoToSignIn}
        className={classes.navigationActionsContainerDesktop}
      />
    </ClientAuthContainer>
  );
};

export default ForgotPasswordForm;
